import "./TabelaExtrato.css";
import { filterLicensedLedgerEntries } from "../../../utils/licensedLedgerVisibility";
import { formatLedgerStatementDate } from "../../../utils/ledgerEntryDate";

const CLIENT_NAME_MAX_LENGTH = 48;

function formatClientDisplayName(value, maxLength = CLIENT_NAME_MAX_LENGTH) {
  const text = String(value || "").trim();
  if (!text) return "-";
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength).trimEnd()}...`;
}

function extractInstallmentLabel(description) {
  const match = String(description || "").match(/PARCELA\s+(\d+)\s*\/\s*(\d+)/i);
  if (!match) return "";
  return `Parcela ${match[1]}/${match[2]}`;
}

function formatOrderNumber(entry) {
  const orderNumber = String(entry?.order_number || "").trim();
  const installmentLabel = extractInstallmentLabel(entry?.description);

  if (!orderNumber && !installmentLabel) return "-";
  if (!installmentLabel) return orderNumber || "-";
  if (!orderNumber) return installmentLabel;
  return `${orderNumber} (${installmentLabel})`;
}

const TabelaExtrato = ({ itensLista = [], loading = false, children, pagination }) => {
  const itens = filterLicensedLedgerEntries(itensLista);
  const showPagination = Boolean(pagination);
  const totalItems = Number(pagination?.totalItems ?? itens.length);
  const currentPage = Number(pagination?.currentPage || 1);
  const totalPages = Number(pagination?.totalPages || 1);
  const pageSize = Number(pagination?.pageSize || itens.length || 1);
  const startItem =
    totalItems === 0 ? 0 : Number(pagination?.startItem ?? (currentPage - 1) * pageSize + 1);
  const endItem = Number(pagination?.endItem ?? Math.min(currentPage * pageSize, totalItems));

  const formatCurrencyBRL = (value) => {
    const n = Number(value || 0);
    return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  };

  const renderizarStatus = (status) => {
    switch (status) {
      case "CONFIRMED":
        return <span className="status-pill status-credito">Confirmado</span>;
      case "PENDING":
        return <span className="status-pill status-analise">Pendente</span>;
      case "OVERDUE":
        return <span className="status-pill status-cancelado">Em atraso</span>;
      case "AWAITING_CONFIRMATION":
        return (
          <span className="status-pill status-aguardando-confirmacao">
            Aguardando confirmação
          </span>
        );
      case "WITHDRAW_REQUESTED":
      case "REQUESTED":
        return <span className="status-pill status-saque-solicitado">Saque solicitado</span>;
      case "CANCELED":
      case "CANCELLED":
        return <span className="status-pill">Cancelado</span>;
      default:
        return <span className="status-pill">-</span>;
    }
  };

  return (
    <section className="table-card">
      <div className="table-title">{children}</div>

      <div className="table-tr">
        <table>
          <thead>
            <tr>
              <th>Data</th>
              <th>Nº compra</th>
              <th>Cliente</th>
              <th>Valor compra</th>
              <th>Valor comissão</th>
              <th>Tipo</th>
              <th>Status</th>
            </tr>
          </thead>

          <tbody id="extratoTableBody">
            {loading && (
              <tr>
                <td colSpan={7} style={{ textAlign: "center" }}>
                  Carregando...
                </td>
              </tr>
            )}

            {!loading && itens.length === 0 && (
              <tr>
                <td colSpan={7} style={{ textAlign: "center" }}>
                  Nenhum lançamento encontrado
                </td>
              </tr>
            )}

            {!loading &&
              itens.map((item) => {
                const isSaque = item.origin === "WITHDRAW";
                const clienteCompleto = String(item?.client_name || "").trim() || "-";
                const clienteExibido = formatClientDisplayName(clienteCompleto);

                const purchaseAmount = Number(item.purchase_amount) || 0;
                const commissionPercent = Number(item.commission_percent) || 0;
                const amountValue = Number(item.amount);
                const commissionValue =
                  item?.amount !== null &&
                  item?.amount !== undefined &&
                  item?.amount !== "" &&
                  Number.isFinite(amountValue)
                    ? amountValue
                    : (purchaseAmount * commissionPercent) / 100;
                const commissionFinal = isSaque ? -purchaseAmount : commissionValue;
                return (
                  <tr key={item.id}>
                    <td>{formatLedgerStatementDate(item)}</td>
                    <td>{formatOrderNumber(item)}</td>
                    <td className="cliente-cell" title={clienteCompleto}>
                      <span className="cliente-text">{clienteExibido}</span>
                    </td>
                    <td>{formatCurrencyBRL(purchaseAmount)}</td>
                    <td>{formatCurrencyBRL(commissionFinal)}</td>
                    <td className={isSaque ? "tipo-debito" : "tipo-credito"}>
                      {isSaque ? "Débito" : "Crédito"}
                    </td>
                    <td>{renderizarStatus(item.status)}</td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {showPagination && (
        <div className="table-footer">
          <span>
            Mostrando {startItem} a {endItem} de {totalItems} lancamento(s)
          </span>

          <div className="pagination" aria-label="Paginacao do extrato">
            <button
              type="button"
              onClick={pagination.onPrevious}
              disabled={loading || !pagination.hasPrevious}
            >
              Anterior
            </button>
            <span id="currentPageLabel">
              Pagina {currentPage} de {totalPages}
            </span>
            <button
              type="button"
              onClick={pagination.onNext}
              disabled={loading || !pagination.hasNext}
            >
              Proxima
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default TabelaExtrato;
